import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type KeyboardEvent as ReactKeyboardEvent,
  type SVGProps,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { Building2, LogOut, Search } from 'lucide-react';
import type { TenantSummary } from '@vce/shared';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

type NavIcon = ComponentType<SVGProps<SVGSVGElement>>;

export interface PaletteNavItem {
  to: string;
  label: string;
  icon: NavIcon;
  /** Grupo pai na sidebar, ex.: "Revisão" — usado como subtítulo na busca. */
  group?: string;
}

type PaletteResult =
  | { id: string; kind: 'nav'; to: string; label: string; icon: NavIcon; hint?: string }
  | { id: string; kind: 'tenant'; tenantId: string; label: string; hint: string }
  | { id: string; kind: 'product'; productId: number; label: string; hint: string }
  | { id: string; kind: 'logout'; label: string };

export function commandShortcutLabel(): string {
  return /Mac|iPhone|iPad|iPod/.test(navigator.userAgent) ? '⌘K' : 'Ctrl+K';
}

export function CommandPalette({
  open,
  onOpenChange,
  navItems,
  tenants,
  tenantId,
  canSearchProducts,
  onNavigate,
  onSelectTenant,
  onLogout,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  navItems: PaletteNavItem[];
  tenants: TenantSummary[];
  tenantId: string | null;
  canSearchProducts: boolean;
  onNavigate(to: string): void;
  onSelectTenant(tenantId: string): void;
  onLogout(): void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return;
      event.preventDefault();
      onOpenChange(!open);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setDebounced('');
    setSelected(0);
    const id = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, [open]);

  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(query.trim()), 250);
    return () => window.clearTimeout(handle);
  }, [query]);

  const products = useQuery({
    queryKey: ['products', tenantId, 'palette', debounced],
    queryFn: () =>
      api.catalog.products(tenantId as string, {
        search: debounced,
        page: 1,
        pageSize: 8,
      }),
    enabled: open && canSearchProducts && Boolean(tenantId) && debounced.length >= 2,
  });

  const results = useMemo<PaletteResult[]>(() => {
    const needle = query.trim().toLowerCase();
    const matches = (value: string): boolean => !needle || value.toLowerCase().includes(needle);

    const nav: PaletteResult[] = navItems
      .filter(
        (item) =>
          matches(item.label) || (item.group !== undefined && matches(item.group)),
      )
      .map((item) => ({
        id: `nav:${item.to}`,
        kind: 'nav' as const,
        to: item.to,
        label: item.label,
        icon: item.icon,
        ...(item.group ? { hint: item.group } : {}),
      }));

    const tenantResults: PaletteResult[] =
      tenants.length > 1
        ? tenants
            .filter((item) => matches(item.name) || matches(item.accountName))
            .map((item) => ({
              id: `tenant:${item.id}`,
              kind: 'tenant',
              tenantId: item.id,
              label: item.name,
              hint: item.isSandbox ? `${item.accountName} · sandbox` : item.accountName,
            }))
        : [];

    const productResults: PaletteResult[] = (products.data?.items ?? []).map((item) => ({
      id: `product:${item.productId}`,
      kind: 'product',
      productId: item.productId,
      label: item.name,
      hint: item.brandName ? `${item.brandName} · #${item.productId}` : `#${item.productId}`,
    }));

    const logout: PaletteResult[] = matches('sair') || matches('logout')
      ? [{ id: 'logout', kind: 'logout', label: 'Sair' }]
      : [];

    return [...nav, ...tenantResults, ...productResults, ...logout];
  }, [navItems, products.data?.items, query, tenants]);

  useEffect(() => {
    setSelected(0);
  }, [query, results.length]);

  function run(result: PaletteResult | undefined): void {
    if (!result) return;
    onOpenChange(false);
    if (result.kind === 'nav') onNavigate(result.to);
    if (result.kind === 'tenant') onSelectTenant(result.tenantId);
    if (result.kind === 'product') onNavigate(`/products?productId=${result.productId}`);
    if (result.kind === 'logout') onLogout();
  }

  function onInputKeyDown(event: ReactKeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelected((index) => (results.length === 0 ? 0 : (index + 1) % results.length));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelected((index) =>
        results.length === 0 ? 0 : (index - 1 + results.length) % results.length,
      );
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      run(results[selected]);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[70] bg-black/70" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-[min(20vh,8rem)] z-[70] w-[min(100%-1.5rem,32rem)] -translate-x-1/2 overflow-hidden rounded-2xl border border-[var(--color-hairline)] bg-[var(--color-surface-card)] outline-none"
        >
          <Dialog.Title className="sr-only">Busca rápida</Dialog.Title>
          <div className="flex items-center gap-2 border-b border-[var(--color-hairline)] px-3">
            <Search className="size-4 shrink-0 text-[var(--color-muted)]" aria-hidden />
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onInputKeyDown}
              placeholder="Ir para página, conta ou produto…"
              className="h-12 min-w-0 flex-1 bg-transparent text-sm text-[var(--color-ink)] outline-none placeholder:text-[var(--color-muted)]"
              aria-label="Buscar"
              aria-autocomplete="list"
              aria-controls="command-palette-results"
              aria-activedescendant={results[selected] ? `palette-${results[selected].id}` : undefined}
            />
            <kbd className="hidden rounded border border-[var(--color-hairline)] px-1.5 py-0.5 text-[10px] font-medium text-[var(--color-muted)] sm:inline">
              esc
            </kbd>
          </div>
          <ul
            id="command-palette-results"
            role="listbox"
            className="max-h-[min(24rem,50vh)] overflow-y-auto p-1.5"
          >
            {results.length === 0 ? (
              <li className="px-3 py-6 text-center text-sm text-[var(--color-muted)]">
                Nenhum resultado.
              </li>
            ) : (
              results.map((item, index) => {
                const active = index === selected;
                return (
                  <li key={item.id} role="option" id={`palette-${item.id}`} aria-selected={active}>
                    <button
                      type="button"
                      onMouseEnter={() => setSelected(index)}
                      onClick={() => run(item)}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-[10px] px-3 py-2 text-left text-sm',
                        active
                          ? 'bg-[var(--color-surface-elevated)] text-[var(--color-ink)]'
                          : 'text-[var(--color-body)]',
                      )}
                    >
                      <ResultIcon item={item} />
                      <span className="min-w-0 flex-1 truncate font-medium">{item.label}</span>
                      {'hint' in item ? (
                        <span className="max-w-[40%] truncate text-[11px] text-[var(--color-muted)]">
                          {item.hint}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ResultIcon({ item }: { item: PaletteResult }) {
  if (item.kind === 'nav') {
    const Icon = item.icon;
    return <Icon className="size-4 shrink-0" aria-hidden />;
  }
  if (item.kind === 'tenant') {
    return <Building2 className="size-4 shrink-0" aria-hidden />;
  }
  if (item.kind === 'logout') {
    return <LogOut className="size-4 shrink-0" aria-hidden />;
  }
  return <Search className="size-4 shrink-0" aria-hidden />;
}
